import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, PRODUCT_KINDS, type ProductUpdateV2 } from '@gk/contracts'
import { unitSymbol, type ProductKind, type ProductRow, type ProductVersionRow, type SupplierRow } from '../../data/catalog.ts'
import {
  addDays, batchProblems, batchRecordPayload, densityApplies, densityInput, densityOf, MAX_EXPIRY_DAYS, MIN_DAY, newProductProblems, productChanges, productDefinePayload,
  productRevisePayload, productUnitGroups, reviseProblems, rowsOfFormulation, unitOptionLabel, versionContent, type FormulationRow,
} from '../../data/catalogueUi.ts'
import { quantityText } from '../../data/stockUi.ts'
import { fileLinkOf, optionText, titleOf, useVaultDocuments, useVaultLinks, useVaultVersions, versionOptions } from '../../data/documents.ts'
import { FileLine } from '../Documents.tsx'
import { Banner, Field } from '../../ui/components.tsx'
import type { useWorkshopAction } from '../../ui/hooks.ts'
import { Feedback, FormulationEditor, Problems, SupplierSelect, useLang, type Access } from './shared.tsx'

type Action = ReturnType<typeof useWorkshopAction>
const cancelBtn = 'btn !min-h-11'

/** The density and its source, with what they are for. Shown only for a product counted by mass or by volume. */
function DensityFields({ idBase, density, source, onDensity, onSource }: { idBase: string; density: string; source: string; onDensity: (v: string) => void; onSource: (v: string) => void }) {
  const { t } = useTranslation()
  return (
    <>
      <Field label={t('catalogue.form.density')} hint={t('catalogue.form.densityHint')} htmlFor={`${idBase}-density`}>
        <input id={`${idBase}-density`} className="input" inputMode="decimal" autoComplete="off" maxLength={12} value={density} onChange={(e) => onDensity(e.target.value)} data-testid={`${idBase}-density`} />
      </Field>
      <Field label={t('catalogue.form.densitySource')} hint={t('catalogue.form.densitySourceHint')} htmlFor={`${idBase}-density-source`}>
        <input id={`${idBase}-density-source`} className="input" maxLength={200} value={source} onChange={(e) => onSource(e.target.value)} data-testid={`${idBase}-density-source`} />
      </Field>
    </>
  )
}

/** M15-V: the label a product version points at (`document_version_id`): the labels IN FORCE of Réglages › Documents, by title, with their file's type and size;
 *  "aucune" by default. A version already pointing at a label that was replaced since keeps it until another is chosen (shown as such: never dropped by a
 *  revision that did not mean to). A part, counted by the piece, has no label to point at. Reads the mirror (offline). */
export function LabelPicker({ idBase, value, onChange, kept }: { idBase: string; value: string; onChange: (v: string) => void; kept?: string | null }) {
  const { t } = useTranslation()
  const lang = useLang()
  const docs = useVaultDocuments(); const versions = useVaultVersions(); const links = useVaultLinks()
  const options = useMemo(() => versionOptions(docs ?? [], versions ?? [], links ?? [], 'label', lang), [docs, versions, links, lang])
  const keptRow = kept && !options.some((o) => o.id === kept) ? (versions ?? []).find((v) => v.id === kept) : undefined
  const versionWord = (n: number) => t('documents.version', { n })
  return (
    <Field label={t('documents.labelPicker.label')} hint={options.length > 0 ? t('documents.labelPicker.hint') : t('documents.labelPicker.none')} htmlFor={`${idBase}-label`}>
      <select id={`${idBase}-label`} className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${idBase}-label`}>
        <option value="">{t('documents.labelPicker.noLabel')}</option>
        {kept && !keptRow && !options.some((o) => o.id === kept) && <option value={kept}>{t('documents.labelPicker.keptUnknown')}</option>}
        {keptRow && <option value={keptRow.id}>{titleOf(keptRow.title, lang)} · {versionWord(keptRow.version_no)} ({t('documents.replaced')})</option>}
        {options.map((o) => <option key={o.id} value={o.id}>{optionText(o, lang, versionWord)}</option>)}
      </select>
    </Field>
  )
}

/** M15-V: the document a product version points at, on the product's page. A label of Réglages › Documents with its file: its title, its version (said
 *  replaced once it left force) and the file to download (never previewed). A document of text alone, or one this device does not hold, keeps the old link
 *  to the document's page. */
export function VersionDocument({ documentVersionId, fallback }: { documentVersionId: string; fallback: React.ReactNode }) {
  const { t } = useTranslation()
  const lang = useLang()
  const versions = useVaultVersions(); const links = useVaultLinks()
  const v = (versions ?? []).find((x) => x.id === documentVersionId)
  const file = v ? fileLinkOf(links ?? [], v.id) : undefined
  if (!v || !file) return <>{fallback}</>
  const title = titleOf(v.title, lang)
  return (
    <div className="flex flex-col gap-1" data-testid="version-label" data-version={v.id} data-status={v.status}>
      <p className="text-sm"><span className="font-semibold">{t('documents.kindOne.label')}</span> : {title} · {t('documents.version', { n: v.version_no })}{v.status === 'retired' ? ` (${t('documents.replaced')})` : ''}</p>
      <FileLine file={file} title={title} versionNo={v.version_no} testId="version-label-file" />
    </div>
  )
}

/** A new product and its first version (GK-M9). The base unit never changes afterwards, so it is chosen here, from the units a product can be measured in. */
export function NewProductForm({ suppliers, w, access, onClose, onCreated }: { suppliers: readonly SupplierRow[]; w: Action; access: Access; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation()
  const lang = useLang()
  const ids = useRef({ product: newId(), version: newId() })
  const [kind, setKind] = useState<ProductKind>('part')
  const [name, setName] = useState('')
  const [unit, setUnit] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [reference, setReference] = useState('')
  const [amm, setAmm] = useState('')
  const [density, setDensity] = useState('')
  const [source, setSource] = useState('')
  const [rows, setRows] = useState<FormulationRow[]>([])
  const [label, setLabel] = useState('')
  const groups = useMemo(productUnitGroups, [])
  const applies = unit !== '' && densityApplies(unit)
  const input = { name, unit, density, source, rows, densityApplies: applies }
  const problems = newProductProblems(input)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || !access.canSave) return
    const productId = ids.current.product
    // M15-V: the first version may point at a label of Réglages › Documents
    const payload = { ...productDefinePayload({ productId, versionId: ids.current.version, kind, name, unit, supplierId, reference, amm, content: versionContent(input) }), ...(label && kind !== 'part' ? { document_version_id: label } : {}) }
    void w.run('product.define@1', payload, 'catalogue.created', { aggregateId: productId })
      .then((ok) => { if (ok) { ids.current = { product: newId(), version: newId() }; onCreated(productId) } })
  }
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4" data-testid="product-form">
      <button type="button" className="self-start text-sm font-semibold underline" onClick={onClose} data-testid="product-form-back">← {t('common.back')}</button>
      <h2 className="text-xl font-bold">{t('catalogue.products.new')}</h2>
      <Field label={t('catalogue.form.kind')} htmlFor="np-kind">
        <select id="np-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as ProductKind)} data-testid="product-kind">
          {PRODUCT_KINDS.map((k) => <option key={k} value={k}>{t(`catalogue.kind.${k}`)}</option>)}
        </select>
      </Field>
      <Field label={t('catalogue.form.name')} htmlFor="np-name"><input id="np-name" className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="product-name" /></Field>
      <Field label={t('catalogue.form.unit')} hint={t('catalogue.form.unitHint')} htmlFor="np-unit">
        <select id="np-unit" className="input" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="product-unit">
          <option value="">{t('catalogue.form.pickUnit')}</option>
          {groups.map((g) => (
            <optgroup key={g.dimension} label={t(`catalogue.dimension.${g.dimension}`)}>
              {g.units.map((u) => <option key={u.code} value={u.code}>{unitOptionLabel(u, lang)}</option>)}
            </optgroup>
          ))}
        </select>
      </Field>
      <Field label={t('catalogue.form.supplier')} htmlFor="np-supplier"><SupplierSelect id="np-supplier" value={supplierId} onChange={setSupplierId} suppliers={suppliers} testId="product-supplier" /></Field>
      <Field label={t('catalogue.form.reference')} htmlFor="np-ref"><input id="np-ref" className="input" maxLength={60} value={reference} onChange={(e) => setReference(e.target.value)} data-testid="product-reference" /></Field>
      {kind === 'ppp' && (
        <Field label={t('catalogue.form.amm')} hint={t('catalogue.form.ammHint')} htmlFor="np-amm"><input id="np-amm" className="input" maxLength={30} value={amm} onChange={(e) => setAmm(e.target.value)} data-testid="product-amm" /></Field>
      )}
      {applies && <DensityFields idBase="product" density={density} source={source} onDensity={setDensity} onSource={setSource} />}
      {unit !== '' && !applies && <p className="meta">{t('catalogue.form.densityNotUsed')}</p>}
      <FormulationEditor rows={rows} onChange={setRows} />
      {kind !== 'part' && <LabelPicker idBase="product" value={label} onChange={setLabel} />}
      <Problems codes={problems} />
      <Feedback id="product-new" error={w.error} pending={w.pending} />
      <button className="btn btn-primary" disabled={w.busy || problems.length > 0 || !access.canSave} data-testid="product-create">{w.busy ? t('common.saving') : t('catalogue.form.create')}</button>
    </form>
  )
}

const updateV2 = (p: Pick<ProductRow, 'id' | 'revision'>, set: ProductUpdateV2['set']): ProductUpdateV2 => ({ product_id: p.id, expected_revision: p.revision, set })

/** The club's minimum as typed ("50", "12,5"): a number with three decimals at most, or empty (no minimum); null when it is not one. */
export function minInput(raw: string): { ok: true; value: number | null } | { ok: false } {
  const text = quantityText(raw)
  if (raw.trim() === '') return { ok: true, value: null }
  if (text === null) return { ok: false }
  const n = Number(text)
  return n >= 0 && n <= 10_000_000 && Math.abs(n * 1000 - Math.round(n * 1000)) < 1e-9 ? { ok: true, value: n } : { ok: false }
}

/** Name, supplier, reference, authorisation number, the club's minimum (M15-A: product.update@2, the one version this build sends; a lowered or cleared minimum
 *  travels as null), archive. The base unit and the type stay as they are: a different one is a new product. */
export function EditProductForm({ product, suppliers, w, access, onDone }: { product: ProductRow; suppliers: readonly SupplierRow[]; w: Action; access: Access; onDone: () => void }) {
  const { t } = useTranslation()
  const lang = useLang()
  const [name, setName] = useState(product.name)
  const [supplierId, setSupplierId] = useState(product.supplier_id ?? '')
  const [reference, setReference] = useState(product.reference ?? '')
  const [amm, setAmm] = useState(product.amm_number ?? '')
  const currentMin = product.min_qty_base === null || product.min_qty_base === undefined || product.min_qty_base === '' ? null : Number(product.min_qty_base)
  const [min, setMin] = useState(currentMin === null ? '' : String(currentMin).replace('.', lang === 'en' ? '.' : ','))
  const parsedMin = minInput(min)
  const set: ProductUpdateV2['set'] = productChanges(product, { name, supplierId, reference, amm })
  if (parsedMin.ok && parsedMin.value !== currentMin) set.min_qty_base = parsedMin.value
  const dirty = Object.keys(set).length > 0
  const archived = !!product.archived_at
  const save = (e: FormEvent) => {
    e.preventDefault()
    if (!dirty || !name.trim() || !parsedMin.ok) return
    void w.run('product.update@2', updateV2(product, set), 'catalogue.edit.saved', { aggregateId: product.id }).then((ok) => { if (ok) onDone() })
  }
  return (
    <form onSubmit={save} noValidate className="flex flex-col gap-3" data-testid="product-edit-form">
      <Field label={t('catalogue.form.name')} htmlFor={`pe-name-${product.id}`}><input id={`pe-name-${product.id}`} className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="product-edit-name" /></Field>
      <Field label={t('catalogue.form.supplier')} htmlFor={`pe-sup-${product.id}`}><SupplierSelect id={`pe-sup-${product.id}`} value={supplierId} onChange={setSupplierId} suppliers={suppliers} testId="product-edit-supplier" /></Field>
      <Field label={t('catalogue.form.reference')} htmlFor={`pe-ref-${product.id}`}><input id={`pe-ref-${product.id}`} className="input" maxLength={60} value={reference} onChange={(e) => setReference(e.target.value)} data-testid="product-edit-reference" /></Field>
      {product.kind === 'ppp' && (
        <Field label={t('catalogue.form.amm')} hint={t('catalogue.form.ammHint')} htmlFor={`pe-amm-${product.id}`}><input id={`pe-amm-${product.id}`} className="input" maxLength={30} value={amm} onChange={(e) => setAmm(e.target.value)} data-testid="product-edit-amm" /></Field>
      )}
      <Field label={t('catalogue.form.min', { unit: unitSymbol(product.base_unit_code, lang) })} hint={t('catalogue.form.minHint')} error={parsedMin.ok ? null : t('parts.quantityInvalid')} htmlFor={`pe-min-${product.id}`}>
        <input id={`pe-min-${product.id}`} className="input" inputMode="decimal" autoComplete="off" value={min} onChange={(e) => setMin(e.target.value)} data-testid="product-edit-min" />
      </Field>
      <p className="meta" data-testid="product-edit-unit">{t('catalogue.edit.unit', { unit: unitSymbol(product.base_unit_code, lang) })} {t('catalogue.edit.unitFixed')}</p>
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary !min-h-11 flex-1" disabled={w.busy || !dirty || !name.trim() || !parsedMin.ok || !access.canSave} data-testid="product-save">{w.busy ? t('common.saving') : t('common.save')}</button>
        <button type="button" className={cancelBtn} onClick={onDone} data-testid="product-edit-cancel">{t('common.cancel')}</button>
      </div>
      <div className="flex flex-col gap-2 border-t-2 border-slate-200 pt-3 dark:border-slate-700">
        <p className="meta">{archived ? t('catalogue.edit.archivedHelp') : t('catalogue.edit.archiveHelp')}</p>
        <button type="button" className="btn !min-h-11" disabled={w.busy || !access.canSave} data-testid="product-archive"
          onClick={() => void w.run('product.update@2', updateV2(product, { archived: !archived }), archived ? 'catalogue.edit.unarchivedDone' : 'catalogue.edit.archivedDone', { aggregateId: product.id }).then((ok) => { if (ok) onDone() })}>
          {archived ? t('catalogue.unarchive') : t('catalogue.archive')}
        </button>
      </div>
    </form>
  )
}

/** A new, immutable version: the whole content of the one in force is shown and can be changed (the server keeps nothing over: what is sent is the new version). The data sheet link is carried over. */
export function ReviseForm({ product, newest, w, access, onDone }: { product: ProductRow; newest: ProductVersionRow; w: Action; access: Access; onDone: () => void }) {
  const { t } = useTranslation()
  const lang = useLang()
  const versionId = useRef(newId())
  const applies = densityApplies(product.base_unit_code) || densityOf(newest) !== null            // a density that is already there can still be corrected or cleared
  const [density, setDensity] = useState(densityInput(densityOf(newest), lang))
  const [source, setSource] = useState(newest.density_source ?? '')
  const [rows, setRows] = useState<FormulationRow[]>(rowsOfFormulation(newest.formulation))
  const [label, setLabel] = useState(newest.document_version_id ?? '')
  const labelChanged = label !== (newest.document_version_id ?? '')
  const input = { density, source, rows, densityApplies: applies }
  // M15-V: a new label alone is a reason for a new version (the same content otherwise)
  const problems = reviseProblems(newest, input).filter((p) => !(labelChanged && p === 'NOTHING_CHANGED'))
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || !access.canSave) return
    void w.run('product.revise@1', { ...productRevisePayload(product.id, versionId.current, newest, versionContent(input)), document_version_id: label || null }, 'catalogue.revise.saved', { aggregateId: product.id })
      .then((ok) => { if (ok) { versionId.current = newId(); onDone() } })
  }
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3" data-testid="product-revise-form">
      <Banner tone="info">{t('catalogue.revise.help', { n: newest.version_no })}</Banner>
      {applies ? <DensityFields idBase="revise" density={density} source={source} onDensity={setDensity} onSource={setSource} /> : <p className="meta">{t('catalogue.form.densityNotUsed')}</p>}
      <FormulationEditor rows={rows} onChange={setRows} />
      {product.kind !== 'part'
        ? <LabelPicker idBase="revise" value={label} onChange={setLabel} kept={newest.document_version_id} />
        : newest.document_version_id && <p className="meta">{t('catalogue.revise.sheetKept')}</p>}
      <Problems codes={problems} />
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary !min-h-11 flex-1" disabled={w.busy || problems.length > 0 || !access.canSave} data-testid="product-revise-save">{w.busy ? t('common.saving') : t('catalogue.revise.save')}</button>
        <button type="button" className={cancelBtn} onClick={onDone} data-testid="product-revise-cancel">{t('common.cancel')}</button>
      </div>
    </form>
  )
}

/** A lot received: its number, its expiry, the day it came in, who delivered it. No stock effect yet (it is a dictionary). */
export function BatchForm({ product, lots, suppliers, today, w, access, onDone }: { product: ProductRow; lots: readonly string[]; suppliers: readonly SupplierRow[]; today: string; w: Action; access: Access; onDone: () => void }) {
  const { t } = useTranslation()
  const id = useRef(newId())
  const [lot, setLot] = useState('')
  const [expiry, setExpiry] = useState('')
  const [received, setReceived] = useState('')
  const [supplierId, setSupplierId] = useState(product.supplier_id ?? '')
  const problems = batchProblems({ lot, expiry, received }, lots, today)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || !access.canSave) return
    void w.run('batch.record@1', batchRecordPayload({ batchId: id.current, productId: product.id, lot, expiry, received, supplierId }), 'catalogue.batch.saved', { aggregateId: id.current })
      .then((ok) => { if (ok) { id.current = newId(); onDone() } })
  }
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3" data-testid="batch-form">
      <Field label={t('catalogue.batch.lot')} htmlFor={`bl-${product.id}`}><input id={`bl-${product.id}`} className="input" maxLength={60} value={lot} onChange={(e) => setLot(e.target.value)} data-testid="batch-lot" /></Field>
      <Field label={t('catalogue.batch.expiryDate')} hint={t('common.optional')} htmlFor={`be-${product.id}`}>
        <input id={`be-${product.id}`} type="date" className="input" min={MIN_DAY} max={addDays(today, MAX_EXPIRY_DAYS)} value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="batch-expiry" />
      </Field>
      <Field label={t('catalogue.batch.receivedOn')} hint={t('common.optional')} htmlFor={`br-${product.id}`}>
        <input id={`br-${product.id}`} type="date" className="input" min={MIN_DAY} max={today} value={received} onChange={(e) => setReceived(e.target.value)} data-testid="batch-received" />
      </Field>
      <Field label={t('catalogue.form.supplier')} htmlFor={`bs-${product.id}`}><SupplierSelect id={`bs-${product.id}`} value={supplierId} onChange={setSupplierId} suppliers={suppliers} testId="batch-supplier" /></Field>
      <Problems codes={problems} />
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary !min-h-11 flex-1" disabled={w.busy || problems.length > 0 || !access.canSave} data-testid="batch-save">{w.busy ? t('common.saving') : t('catalogue.batch.save')}</button>
        <button type="button" className={cancelBtn} onClick={onDone} data-testid="batch-cancel">{t('common.cancel')}</button>
      </div>
    </form>
  )
}
